import { Controller } from '@hotwired/stimulus';
import { application } from 'src';
import { isBlank, isPresent } from '@fretadao/f-js-utils';
import { addClass, hasClass, find, findAll, removeClass } from '@fretadao/f-js-dom';

application.register(
  'admin-selection-dependent',
  class extends Controller {
    REQUIRED_CLASS = 'required';
    REQUIRED_ATTR = 'required';
    SELECT2_CHANGE_EVENTS = ['select2:select', 'select2:unselect', 'select2:clear'];

    // For dependentFieldContainer, add the data-for-value='specific_value' (on container)
    static targets = ['selectContainer', 'dependentFieldContainer'];

    static values = {
      specificOptions: String,
      hideClass: { type: String, default: 'hide' },
      requiredOnShow: { type: Boolean, default: true }
    };

    connect() {
      this.#defineDependentFieldVisibility();

      if (this.#isRadioButtom(this.#selectField())) {
        this.#radioButtomsOnForm().forEach(
          field => field.addEventListener('change', this.#defineDependentFieldVisibility.bind(this))
        );
      } else {
        this.SELECT2_CHANGE_EVENTS.forEach(
          select2Event => this.#selectFieldAsSelect2().on(select2Event, this.#defineDependentFieldVisibility.bind(this))
        );
      }
    }

    disconnect() {
      if (this.#isRadioButtom(this.#selectField())) {
        this.#radioButtomsOnForm().forEach(field => field.removeEventListener('change'));
      } else {
        this.SELECT2_CHANGE_EVENTS.forEach(
          select2Event => this.#selectFieldAsSelect2().off(select2Event, this.#defineDependentFieldVisibility.bind(this))
        );
      }
    }

    #createRequiredAbbrNode() {
      const node = document.createElement('abbr');
      node.setAttribute('title', 'obrigatório');
      node.append('*');

      return node;
    }

    #radioButtomsOnForm() {
      return findAll('input[type=radio]', this.element);
    }

    #selectField() {
      return find('select,input[type=radio]:checked', this.selectContainerTarget);
    }

    #dependentField(target) {
      return find('select,input,textarea', target);
    }

    #currentRequiredAbbrNode(target) {
      return find('abbr', target);
    }

    #isToShow(target) {
      const selectedValues = this.#selectedValues();
      const specificOptions = this.#splitOptions(this.specificOptionsValue || target.dataset.forValue);

      if (isPresent(specificOptions)) {
        return selectedValues.some(selectedValue => specificOptions.includes(selectedValue));
      } else {
        return selectedValues.some(isPresent)
      }
    }

    #defineDependentFieldVisibility() {
      this.dependentFieldContainerTargets.forEach(
        target => { this.#isToShow(target) ? this.#showField(target) : this.#hideField(target) }
      )
    }

    #selectFieldAsSelect2() {
      return $(this.#selectField());
    }

    #hideField(target) {
      addClass(target, this.hideClassValue);
      this.#allowField(target);
      this.#clearField(target);
    }

    #showField(target) {
      this.#requireField(target);
      removeClass(target, this.hideClassValue)
    }

    #requireField(target) {
      if (!this.requiredOnShowValue || !hasClass(target, 'required')) return;

      const dependentField = this.#dependentField(target);
      if (isBlank(this.#currentRequiredAbbrNode(target))) {
        find('label', target).append(this.#createRequiredAbbrNode());
      }

      dependentField.setAttribute(this.REQUIRED_ATTR, true);
      addClass(dependentField, this.REQUIRED_CLASS);
    }

    #allowField(target) {
      const dependentField = this.#dependentField(target);

      this.#currentRequiredAbbrNode(target)?.remove();

      dependentField.removeAttribute(this.REQUIRED_ATTR);
      removeClass(dependentField, this.REQUIRED_CLASS);
    }

    #selectedValues() {
      if (this.#isSelectField(this.#selectField())) {
        return Array.from(this.#selectField().options)
          .filter(option => option.selected)
          .map(option => option.value);
      } else {
        return [this.#selectField().value];
      }
    }

    #splitOptions(options) {
      return isBlank(options) ? [] : options?.split(',');
    }

    #clearField(target) {
      this.#dependentField(target).value = '';
    }

    #isRadioButtom(element) {
      return element.type === 'radio';
    }

    #isSelectField(element) {
      return typeof (element) === 'select';
    }
  }
)
